import { type StoreAnalytics, type StorePrivacy } from 'ecosystem'
import { buildGtmBootstrapScript, resolveDataCollectionConfig } from '../config'

interface GtmBootstrapProps {
  storeId: string
  analytics?: StoreAnalytics
  privacy?: StorePrivacy
}

/**
 * The standard GTM snippet, server-rendered as a real `<script>` tag — used when
 * `analytics.loadStrategy` is `HTML`. Renders nothing for the other strategies,
 * where `DataLayerManager` injects client-side instead.
 *
 * Belongs as the FIRST child of `<body>`: the browser has to parse everything
 * above it before the container starts loading, so anything later throws away
 * the timing this exists for.
 *
 * A plain `<script>`, deliberately not `next/script`:
 *  - `beforeInteractive` renders a `<script>` element anyway (React 19 rejects
 *    it) and queues the body onto `self.__next_s` for Next's runtime, so it is
 *    not a native tag;
 *  - `afterInteractive` injects from a child effect, which React runs BEFORE the
 *    `GTMProvider` effect — the container would then load before the Consent
 *    Mode default is set.
 */
export function GtmBootstrap({ storeId, analytics, privacy }: GtmBootstrapProps) {
  const config = resolveDataCollectionConfig({ storeId, analytics, privacy })

  if (config.loadStrategy !== 'HTML' || !config.gtmConfig.centralContainerId) {
    return null
  }

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: buildGtmBootstrapScript(config) }} />
      <noscript>
        <iframe
          src={`${config.scriptOrigin}/ns.html?id=${config.gtmConfig.centralContainerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        />
      </noscript>
    </>
  )
}
